import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { MdVisibility, MdVisibilityOff, MdClose } from "react-icons/md";
import { motion } from "framer-motion";
import { authService, dashboardService } from "../services";
import { toast } from "react-toastify";
import MaxAuthBrandPanel from "../components/auth/MaxAuthBrandPanel";
import MaxAuthMobileBar from "../components/auth/MaxAuthMobileBar";
import SharedModal from "../components/common/SharedModal";
import "../styles/max-auth-pages.css";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordData, setForgotPasswordData] = useState({
    username: "",
    newPassword: "",
    confirmPassword: "",
    resetToken: "",
  });
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState("");

  useEffect(() => {
    if (location.state?.username) {
      setFormData((prev) => ({ ...prev, username: location.state.username }));
    } else {
      const pendingUsername = localStorage.getItem("pendingUsername");
      if (pendingUsername) {
        setFormData((prev) => ({ ...prev, username: pendingUsername }));
      }
    }

    if (location.state?.message) {
      setInfoMessage(location.state.message);
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await authService.login(
        formData.username.trim(),
        formData.password,
        0,
      );

      if (response.token) {
        localStorage.removeItem("pendingTenantId");
        localStorage.removeItem("pendingUsername");

        try {
          await dashboardService.primeNavAppScopeForNewSession();
        } catch (e) {
          console.warn("[Login] Nav scope prefetch failed; home will load scope on demand.", e);
        }

        // Login route is outside NavigationAccessBoundary (see App.js) so nav remount cannot
        // tear down Router/Login mid-submit. /home mounts a fresh boundary with the new session.
        window.dispatchEvent(new CustomEvent("pm_session_identity_changed"));
        window.dispatchEvent(new CustomEvent("pm_nav_scope_refresh"));

        toast.success("Login successfull!");
        navigate("/home");
      } else {
        const msg = "Login failed. Please check your credentials.";
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Login failed. Please check your credentials.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPasswordReset = async () => {
    setForgotPasswordError("");
    setForgotPasswordSuccess("");
    setForgotPasswordLoading(true);

    try {
      const response = await authService.requestPasswordReset(
        forgotPasswordData.username,
      );

      if (response.Success || response.success) {
        const msg =
          response.Message ||
          response.message ||
          "Reset token has been sent. Please check your email.";
        setForgotPasswordSuccess(msg);
        toast.success(msg);
        setForgotPasswordStep(2);
      } else {
        const msg =
          response.Message ||
          response.message ||
          "Failed to request password reset.";
        setForgotPasswordError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to request reset. Please try again.";
      setForgotPasswordError(msg);
      toast.error(msg);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setForgotPasswordError("");
    setForgotPasswordSuccess("");

    if (forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword) {
      setForgotPasswordError("Passwords do not match.");
      return;
    }

    if (forgotPasswordData.newPassword.length < 6) {
      setForgotPasswordError("Password must be at least 6 characters long.");
      return;
    }

    setForgotPasswordLoading(true);

    try {
      const response = await authService.resetPassword(
        forgotPasswordData.username,
        forgotPasswordData.resetToken,
        forgotPasswordData.newPassword,
      );

      if (response.Success || response.success) {
        toast.success("Password reset successfully! You can now login.");
        setForgotPasswordSuccess(
          "Password reset successfully! You can now login.",
        );
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotPasswordStep(1);
          setForgotPasswordData({
            username: "",
            newPassword: "",
            confirmPassword: "",
            resetToken: "",
          });
          setForgotPasswordError("");
          setForgotPasswordSuccess("");
        }, 2000);
      } else {
        const msg =
          response.Message ||
          response.message ||
          response.Error ||
          response.error ||
          "Failed to reset password.";
        setForgotPasswordError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to reset password. Check your token.";
      setForgotPasswordError(msg);
      toast.error(msg);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const itemVariants = {
    hidden: { y: 12, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <div className="max-auth-page">
      <MaxAuthMobileBar />
      <div className="max-auth-layout">
        <MaxAuthBrandPanel variant="login" />

        <div className="max-auth-form-side">
          <div className="max-auth-form-wrap">
            <motion.div
              className="max-auth-card"
              variants={itemVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="max-auth-header">
                <h2 className="max-auth-title">Welcome back</h2>
                <p className="max-auth-subtitle">Sign in to your MaX platform account</p>
              </div>

              <form onSubmit={handleSubmit} className="max-auth-form">
                {infoMessage ? (
                  <div className="max-auth-message max-auth-message--info">{infoMessage}</div>
                ) : null}
                {error ? <div className="max-auth-message max-auth-message--error">{error}</div> : null}

                <div className="max-auth-field">
                  <label className="max-auth-label" htmlFor="username">
                    Username
                  </label>
                  <div className="max-auth-input-wrap">
                    <input
                      className="max-auth-input"
                      id="username"
                      name="username"
                      type="text"
                      placeholder="Enter your username"
                      value={formData.username}
                      onChange={handleChange}
                      required
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="max-auth-field">
                  <label className="max-auth-label" htmlFor="password">
                    Password
                  </label>
                  <div className="max-auth-input-wrap">
                    <input
                      className="max-auth-input"
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="max-auth-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                </div>

                <div className="max-auth-forgot-row">
                  <button
                    type="button"
                    className="max-auth-forgot-btn"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    Forgot password?
                  </button>
                </div>

                <button type="submit" className="max-auth-submit" disabled={loading}>
                  {loading ? "Signing in…" : "Sign in"}
                </button>
              </form>

              <div className="max-auth-footer-text">
                <span>Don&apos;t have an account? </span>
                <Link to="/register" className="max-auth-inline-link">
                  Register for a trial
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <SharedModal
        isOpen={showForgotPassword}
        onClose={() => {
          setShowForgotPassword(false);
          setForgotPasswordStep(1);
          setForgotPasswordError("");
          setForgotPasswordSuccess("");
        }}
        title={forgotPasswordStep === 1 ? "Forgot password" : "Reset password"}
        customWidth="450px"
      >
        <div className="shared-modal-body max-auth-modal__body" style={{padding: '0'}}>
          {forgotPasswordStep === 1 ? (
            <>
              <p className="max-auth-subtitle" style={{ marginBottom: "0.75rem" }}>
                Enter your username to receive a password reset token.
              </p>
              {forgotPasswordError ? (
                <div className="max-auth-message max-auth-message--error">{forgotPasswordError}</div>
              ) : null}
              {forgotPasswordSuccess ? (
                <div className="max-auth-message max-auth-message--info">{forgotPasswordSuccess}</div>
              ) : null}
              <div className="max-auth-field">
                <label className="max-auth-label" htmlFor="fp-username">
                  Username
                </label>
                <input
                  id="fp-username"
                  type="text"
                  className="max-auth-input"
                  value={forgotPasswordData.username}
                  onChange={(e) =>
                    setForgotPasswordData({
                      ...forgotPasswordData,
                      username: e.target.value,
                    })
                  }
                  placeholder="Enter your username"
                  required
                />
              </div>
              <button
                type="button"
                className="max-auth-submit"
                onClick={handleRequestPasswordReset}
                disabled={
                  forgotPasswordLoading || !forgotPasswordData.username.trim()
                }
              >
                {forgotPasswordLoading ? "Requesting…" : "Request reset token"}
              </button>
            </>
          ) : (
            <>
              <p className="max-auth-subtitle" style={{ marginBottom: "0.75rem" }}>
                Enter the reset token you received and your new password.
              </p>
              {forgotPasswordError ? (
                <div className="max-auth-message max-auth-message--error">{forgotPasswordError}</div>
              ) : null}
              {forgotPasswordSuccess ? (
                <div className="max-auth-message max-auth-message--info">{forgotPasswordSuccess}</div>
              ) : null}
              <div className="max-auth-field">
                <label className="max-auth-label" htmlFor="fp-token">
                  Reset token
                </label>
                <input
                  id="fp-token"
                  type="text"
                  className="max-auth-input"
                  value={forgotPasswordData.resetToken}
                  onChange={(e) =>
                    setForgotPasswordData({
                      ...forgotPasswordData,
                      resetToken: e.target.value,
                    })
                  }
                  placeholder="Enter reset token"
                  required
                />
              </div>
              <div className="max-auth-field">
                <label className="max-auth-label" htmlFor="fp-new">
                  New password
                </label>
                <input
                  id="fp-new"
                  type="password"
                  className="max-auth-input"
                  value={forgotPasswordData.newPassword}
                  onChange={(e) =>
                    setForgotPasswordData({
                      ...forgotPasswordData,
                      newPassword: e.target.value,
                    })
                  }
                  placeholder="Enter new password"
                  required
                />
              </div>
              <div className="max-auth-field">
                <label className="max-auth-label" htmlFor="fp-confirm">
                  Confirm password
                </label>
                <input
                  id="fp-confirm"
                  type="password"
                  className="max-auth-input"
                  value={forgotPasswordData.confirmPassword}
                  onChange={(e) =>
                    setForgotPasswordData({
                      ...forgotPasswordData,
                      confirmPassword: e.target.value,
                    })
                  }
                  placeholder="Confirm new password"
                  required
                />
              </div>
              <button
                type="button"
                className="max-auth-submit"
                onClick={handleResetPassword}
                disabled={
                  forgotPasswordLoading ||
                  !forgotPasswordData.resetToken.trim() ||
                  !forgotPasswordData.newPassword.trim() ||
                  !forgotPasswordData.confirmPassword.trim()
                }
              >
                {forgotPasswordLoading ? "Resetting…" : "Reset password"}
              </button>
            </>
          )}
        </div>
      </SharedModal>
    </div>
  );
};

export default Login;
